import {
  appConfigurationQueryOptions,
  useAppConfiguration,
} from "@/api/app-configuration.api";
import { CampaignRunsResponse, useSimpleCampaigns } from "@/api/campaign.api";
import { listLeadsParamsSchema, useLeadDownLoad } from "@/api/leads.api";
import { createUsersQueryOptions } from "@/api/users.api";
import {
  CampaignLeadsGraph,
  CampaignLeadsGraphProps,
} from "@/components/campaign/CampaignLeadsGraph";
import {
  LeadsTable,
  LeadsTableBody,
  LeadsTableHeader,
} from "@/components/common/LeadsTable";
import { Button } from "@/components/generic/Button";
import { Search } from "@/components/generic/Search";
import {
  SelectFilter,
  useSelectFilter,
} from "@/components/generic/SelectFilter";
import { ghostItem } from "@/components/generic/Table";
import { Tooltip } from "@/components/generic/Tooltip";
import { StickyElement } from "@/components/layout/StickyElement";
import { useFileDownload } from "@/hooks/use-file-download";
import { useLeads } from "@/hooks/use-leads";
import { useHorizontalScrollSync } from "@/hooks/use-scroll-sync";
import { trialText } from "@/hooks/use-trial-period";
import { PageHeaderProvider } from "@/providers/PageHeaderProvider";
import { ScrollElementContext } from "@/providers/scroll-element";
import { isAdmin, useIsAdmin } from "@/services/auth.service";
import { useExpectedLeadsFallback } from "@/services/push-message-fallback.service";
import { User } from "@/types/auth";
import { Campaign, Lead } from "@/types/campaign";
import { PagedResult } from "@/utils/api";
import { mapAndExclude } from "@/utils/array-utils";
import { localToUTCDate, UTCWeekPeriod } from "@/utils/dates";
import { store } from "@/utils/jotai";
import { formatNumber } from "@/utils/number-utils";
import { UTCDate } from "@date-fns/utc";
import {
  InfiniteData,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  linkOptions,
  NavigateOptions,
  redirect,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useDebounce } from "@uidotdev/usehooks";
import { endOfWeek, startOfWeek } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { atom } from "jotai";
import { Download, Loader2, LoaderCircleIcon } from "lucide-react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Focusable } from "react-aria";
import { useProductFruitsApi } from "react-product-fruits";
import { SortDescriptor } from "react-stately";
import { twMerge } from "tailwind-merge";
import { useIntersectionObserver } from "usehooks-ts";
import z from "zod";
import { newlyCreatedCampaignIdAtom } from "../-state";

const searchSchema = listLeadsParamsSchema
  .pick({
    name: true,
    configurationName: true,
    status: true,
    orderBy: true,
    desc: true,
  })
  .extend({
    leadsCreatedBefore: z.iso.datetime().optional(),
  });

const leadsCreatedBeforeAtom = atom<Date | null>(null);

export const Route = createFileRoute("/_dashboard/campaigns/$id/")({
  component: RouteComponent,
  pendingMs: 750, // Add a delay before showing the pending state to prevent flickering on fast loads
  pendingMinMs: 1000, // Ensure the loader is shown for at least 1 second to prevent flickering on faster loads
  params: z.object({
    id: z.number().describe("ID of the campaign to edit."),
  }),
  validateSearch: zodValidator(searchSchema),
  pendingComponent: () => (
    <div className="m-auto flex size-full flex-col items-center justify-center gap-y-4 p-7">
      <Loader2 className="size-8 animate-spin" />
      <div>Indlæser kampagne...</div>
    </div>
  ),
  beforeLoad: async ({ context, search, cause }) => {
    const [appConfig] = await Promise.all([
      context.queryClient.ensureQueryData(appConfigurationQueryOptions),
      context.queryClient.ensureInfiniteQueryData({
        ...context.usersQueryOptions,
        revalidateIfStale: false,
      }),
    ]);

    if (cause !== "preload") {
      // Hide leadsCreatedBefore from search and update atom
      if (search.leadsCreatedBefore) {
        store.set(leadsCreatedBeforeAtom, new Date(search.leadsCreatedBefore));
        throw redirect({
          replace: true,
          search: (prev) => ({ ...prev, leadsCreatedBefore: undefined }),
        });
      }

      // Reset leadsCreatedBefore if not in search
      if (!store.get(leadsCreatedBeforeAtom)) {
        store.set(leadsCreatedBeforeAtom, new Date());
      }
    }

    const leadsCreatedBefore = store.get(leadsCreatedBeforeAtom) ?? new Date();

    // Sanitize and update search
    {
      function filterSet<T>(values: Set<T>, validValues: Set<T>) {
        const newValues = new Set<T>();
        for (const value of values) {
          if (validValues.has(value)) {
            newValues.add(value);
          }
        }
        return newValues;
      }

      function compareSets<T>(a: Set<T>, b: Set<T>) {
        if (a.size !== b.size) return false;
        for (const value of a) {
          if (!b.has(value)) return false;
        }
        return true;
      }

      const newSearch: typeof search = {};

      if (search.configurationName) {
        const values = new Set(search.configurationName);
        const newValues = filterSet(
          values,
          new Set(
            context.campaign.configurations
              .map((configuration) => configuration.name || null)
              .filter((name) => name !== null),
          ),
        );
        if (!compareSets(values, newValues)) {
          newSearch.configurationName = newValues.size
            ? [...newValues]
            : undefined;
        }
      }

      if (search.status === undefined) {
        newSearch.status = ["New", "FollowUpDue"];
      } else if (search.status.length > 0) {
        const values = new Set(search.status);
        const newValues = filterSet(
          values,
          new Set<string>(appConfig.leadStatuses.map(({ name }) => name)),
        );
        if (!compareSets(values, newValues)) {
          newSearch.status = [...newValues];
        }
      }

      if (!new Set<unknown>(["created", "followUpTime"]).has(search.orderBy)) {
        newSearch.orderBy = "followUpTime";
      }

      if (search.desc === undefined) {
        newSearch.desc = false;
      }

      if (Object.keys(newSearch).length) {
        throw redirect({
          replace: true,
          from: Route.fullPath,
          search: {
            ...search,
            ...newSearch,
            leadsCreatedBefore: undefined,
          },
        });
      }
    }

    const leadsQueryParams = {
      campaignId: [context.campaign.id],
      createdBefore: leadsCreatedBefore,
      name: search.name,
      configurationName: search.configurationName,
      status: search.status,
      orderBy: search.orderBy,
      desc: search.desc,
    };

    return {
      leadsCreatedBefore,
      leadsQueryParams,
    };
  },
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.prefetchQuery(appConfigurationQueryOptions),
      context.queryClient.ensureInfiniteQueryData({
        ...createUsersQueryOptions({ nameFilter: "" }),
        revalidateIfStale: false,
      }),
      context.queryClient.ensureQueryData(context.campaignRunsQueryOptions),
      context.queryClient.ensureQueryData(
        context.campaignRunMaxLeadsQueryOptions,
      ),
    ]);
    return {
      meta: {
        title: isAdmin(context.user) ? "Leads" : undefined,
      },
    };
  },
  onLeave: ({ params }) => {
    store.set(leadsCreatedBeforeAtom, null);
    store.set(newlyCreatedCampaignIdAtom, (newlyCreatedCampaignId) => {
      return params.id === newlyCreatedCampaignId
        ? null
        : newlyCreatedCampaignId;
    });
  },
});

function RouteComponent() {
  const { id } = Route.useParams();
  const context = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const isAdmin = useIsAdmin();

  const usersQuery = useSuspenseInfiniteQuery(context.usersQueryOptions);

  const { data: campaignRunMaxLeads } = context.useCampaignRunMaxLeadsQuery();

  const getActiveRun = ({ totalRuns, latestRun }: CampaignRunsResponse) => {
    return latestRun && latestRun.completed === null
      ? {
          ...latestRun,
          isFirstRun: totalRuns === 1,
        }
      : null;
  };
  const { data: activeRun } = useSuspenseQuery(
    {
      ...context.campaignRunsQueryOptions,
      select: (data) => getActiveRun(data),
    },
    context.queryClient,
  );

  const notEnoughLeadsToBoost = useMemo(
    () =>
      !!campaignRunMaxLeads &&
      campaignRunMaxLeads.max <= 0 &&
      campaignRunMaxLeads.reason === "NotEnoughLeads",
    [campaignRunMaxLeads],
  );

  const simpleCampaigns = useSimpleCampaigns();
  const { data: campaign } = context.useCampaignQuery();
  useEffect(() => {
    console.debug("activeRun changed:", activeRun);
  }, [activeRun]);
  const [sendFirstLeadReceived, setSendFirstLeadReceived] = useState(false);

  const { data: appConfig } = useAppConfiguration();

  const { leadsQuery, leads, pushMessageLeads } = useLeads({
    campaignId: id,
    params: context.leadsQueryParams,
    ghosts: true,
    onFirstLeadReceived: () => {
      if (!sendFirstLeadReceived) {
        setSendFirstLeadReceived(true);
        console.debug("First lead received - RouteComponent");
      }
    },
  });
  const totalLeadsCount = useMemo(() => {
    return (
      (leadsQuery.data?.pages.at(0)?.resultsFound ?? 0) +
      pushMessageLeads.length
    );
  }, [leadsQuery.data?.pages, pushMessageLeads.length]);

  const { refetch: download } = useLeadDownLoad(leadsQuery.latestDataParams);

  const { download: downLoadFile, isDownloading } = useFileDownload(
    download,
    `leads`,
  );

  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);

  useHorizontalScrollSync(headerScrollRef, bodyScrollRef);

  const [searchString, setSearchString] = useState("");

  const configurationFilter = useSelectFilter({
    items: useMemo(
      () =>
        campaign?.configurations
          ?.map(({ name }) =>
            name ? { key: name, label: name, value: name } : null,
          )
          .filter((item) => item !== null) ?? [],
      [campaign?.configurations],
    ),
  });

  const statusFilter = useSelectFilter({
    items: useMemo(
      () =>
        mapAndExclude(appConfig?.leadStatuses ?? [], (exclude, status) =>
          status.filterable
            ? {
                key: status.name,
                label: status.displayName,
                value: status.name,
              }
            : exclude,
        ),
      [appConfig],
    ),
  });

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "followUpTime",
    direction: "ascending",
  });

  let skipSearchUpdate = false;

  useEffect(() => {
    if (search.name) {
      setSearchString(search.name);
    }

    if (search.configurationName) {
      configurationFilter.setSelectedKeys(new Set(search.configurationName));
    }

    if (search.status) {
      statusFilter.setSelectedKeys(new Set(search.status));
    }

    if (search.orderBy || search.desc !== undefined) {
      setSortDescriptor({
        column: search.orderBy ?? "followUpTime",
        direction: (search.desc ?? false) ? "descending" : "ascending",
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    skipSearchUpdate = true;
  }, []);

  const newSearch = useMemo(() => {
    return {
      name: searchString || undefined,
      configurationName: configurationFilter.selectedValues.length
        ? configurationFilter.selectedValues
        : undefined,
      status: statusFilter.selectedValues,
      orderBy: sortDescriptor.column.toString(),
      desc: sortDescriptor.direction === "descending",
    } satisfies typeof search;
  }, [
    searchString,
    configurationFilter.selectedValues,
    statusFilter.selectedValues,
    sortDescriptor.column,
    sortDescriptor.direction,
  ]);

  const debouncedNewSearch = useDebounce(newSearch, 300);

  useEffect(() => {
    if (skipSearchUpdate) return;

    let diffFound = false;

    for (const key of Object.keys(newSearch) as (keyof typeof newSearch)[]) {
      const newValue = newSearch[key];
      const oldValue = search[key];
      if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
        diffFound = true;
        break;
      }
    }

    if (diffFound) {
      navigate({
        from: Route.fullPath,
        search: (prev) => ({
          ...prev,
          ...newSearch,
          leadsCreatedBefore: new Date().toISOString(),
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipSearchUpdate, debouncedNewSearch]);

  useProductFruitsApi(
    (api) => {
      if (sendFirstLeadReceived && simpleCampaigns?.length === 1) {
        console.debug("Starting first lead received tour");
        api.tours.tryStartTour(124202); // First lead received tour
      }
    },
    [sendFirstLeadReceived, simpleCampaigns],
  );

  const scrollEl = useContext(ScrollElementContext);

  const [selectedWeek, setSelectedWeek] = useState<UTCWeekPeriod | null>(null);

  const [_, setTrialCTAModalProps] = context.useTrialCTAModal();

  const onAdjustWeekSchedule = useMemo<
    CampaignLeadsGraphProps["onAdjustWeekSchedule"]
  >(() => {
    if (!isAdmin) return undefined;
    if (campaign.type !== "Scheduled") return undefined;

    if (context.trialPeriod)
      return () => {
        setTrialCTAModalProps({
          title: trialText,
        });
      };

    return ({ campaignId, week }) => {
      navigate({
        to: "/campaigns/$id/adjust-week/$weekStart",
        params: {
          id: campaignId,
          weekStart: week.start,
        },
      });
    };
  }, [
    campaign.type,
    context.trialPeriod,
    isAdmin,
    navigate,
    setTrialCTAModalProps,
  ]);

  const errorText = useMemo(() => {
    if (leadsQuery.data?.pages.length !== 1) return "";
    const [{ error }] = leadsQuery.data.pages;
    return error?.message ?? "";
  }, [leadsQuery.data?.pages]);

  const leadsTable = useCampaignLeadsTable({
    campaign,
    usersQuery,
    sortDescriptor,
    setSortDescriptor,
    scrollEl,
    leads,
    isFetching: leadsQuery.isFetching,
    errorText,
    fetchNextPage: leadsQuery.fetchNextPage,
    setTrialCTAModalProps,
  });

  const boostButton = useMemo(() => {
    if (!isAdmin || !campaign || campaign.type === "Triggered") return false;

    let isDisabled = false;
    let tooltipText: React.ReactNode = (
      <>
        Vælg et antal leads og modtag <br />
        dem med det samme.
      </>
    );

    if (activeRun) {
      isDisabled = true;
      tooltipText =
        "Du kan ikke lave en manuel levering lige nu, da kampagnen er i gang med at hente leads.";
    } else if (notEnoughLeadsToBoost) {
      isDisabled = true;
      tooltipText =
        "Du kan ikke modtage flere leads, da du har modtaget alle i målgruppen.";
    }

    return {
      tooltipText,
      buttonProps: context.trialPeriod
        ? {
            isDisabled,
            onPress: () => {
              setTrialCTAModalProps({
                title: trialText,
              });
            },
          }
        : {
            isDisabled,
            link: linkOptions({
              from: "/",
              to: "/campaigns/$id/boost",
              params: {
                id: campaign.id,
              },
            }),
            "data-lso-campaign-boost-button": true,
          },
    };
  }, [
    activeRun,
    campaign,
    context.trialPeriod,
    isAdmin,
    notEnoughLeadsToBoost,
    setTrialCTAModalProps,
  ]);

  const playerButton = useMemo(() => {
    if (!campaign) return false;
    const hasLeads = leads.length > 0;
    return {
      isDisabled: !hasLeads,
      onPress: () => {
        if (context.trialPeriod?.isExpired) {
          setTrialCTAModalProps({
            title:
              "Din prøveperiode er udløbet. Kontakt os for at få adgang til Lasso Sales uden begrænsninger.",
          });
          return;
        }

        navigate({
          from: Route.fullPath,
          to: "/player",
          params: {
            id: undefined,
          },
          search: {
            ...search,
            campaignId: [campaign.id],
            createdBefore: context.leadsCreatedBefore.toISOString(),
            returnTo: "campaign",
          },
        });
      },
    };
  }, [
    campaign,
    leads.length,
    context.trialPeriod?.isExpired,
    context.leadsCreatedBefore,
    navigate,
    search,
    setTrialCTAModalProps,
  ]);

  const pageHeaderOptions = useMemo(() => {
    return {
      title: isAdmin ? "Leads" : campaign.name || "Kampagne",
      actions: (
        <>
          {boostButton && (
            <Tooltip tooltipText={boostButton.tooltipText} placement="bottom">
              <Focusable>
                <Button
                  size="lg"
                  intent="secondary"
                  {...boostButton.buttonProps}
                >
                  Manuel levering
                </Button>
              </Focusable>
            </Tooltip>
          )}

          {playerButton && (
            <Button size="lg" {...playerButton}>
              Vis leadkort{" "}
              {leadsQuery.data &&
                `(${formatNumber(Math.min(totalLeadsCount, 1000))}${totalLeadsCount > 1000 ? "+" : ""})`}
            </Button>
          )}
        </>
      ),
    };
  }, [
    boostButton,
    campaign.name,
    isAdmin,
    leadsQuery.data,
    playerButton,
    totalLeadsCount,
  ]);

  const minDate = useMemo(
    () =>
      startOfWeek(new UTCDate(localToUTCDate(new Date(campaign.created))), {
        weekStartsOn: 1,
      }),
    [campaign.created],
  );

  const maxDate = useMemo(
    () =>
      campaign.type === "OnDemand"
        ? endOfWeek(new UTCDate(localToUTCDate(new Date())), {
            weekStartsOn: 1,
          })
        : undefined,
    [campaign.type],
  );

  useExpectedLeadsFallback({
    campaignId: id,
    params: context.leadsQueryParams,
  });

  return (
    <PageHeaderProvider options={pageHeaderOptions}>
      <CampaignLeadsGraph
        campaigns={[campaign]}
        isSingleCampaign
        noDataText="Endnu ingen leads"
        futureLeads={campaign.estimatedRemaining ?? undefined}
        selectedWeek={selectedWeek}
        setSelectedWeek={setSelectedWeek}
        onAdjustWeekSchedule={onAdjustWeekSchedule}
        alignment={campaign.type === "OnDemand" ? "right" : "center"}
        minDate={minDate}
        maxDate={maxDate}
      />

      <motion.div
        key="leads-table"
        className="flex flex-col"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <LeadsTable
          {...leadsTable.props}
          className="flex max-w-full grow flex-col"
        >
          <StickyElement
            stickyProps={{
              className: "bg-theme-background",
            }}
          >
            <div
              id="scroll-headers"
              className="overflow-x-scroll px-8 xl:px-14"
            >
              <div className="mt-4 flex items-center gap-x-6">
                <Search
                  className="-mx-2 min-w-[200px] grow"
                  value={searchString}
                  onChange={(e) => setSearchString(e.currentTarget.value)}
                />

                <div className="flex items-center gap-x-6">
                  <SelectFilter
                    buttonLabel="Type"
                    selectionMode="multiple"
                    items={configurationFilter.items}
                    selectedKeys={configurationFilter.selectedKeys}
                    onSelectionChange={configurationFilter.setSelectedKeys}
                  />

                  <SelectFilter
                    buttonLabel="Status"
                    selectionMode="multiple"
                    items={statusFilter.items}
                    selectedKeys={statusFilter.selectedKeys}
                    onSelectionChange={statusFilter.setSelectedKeys}
                  />

                  <Button
                    isDisabled={leadsTable.leads.length === 0 || isDownloading}
                    onClick={() => {
                      if (context.trialPeriod) {
                        setTrialCTAModalProps({
                          title: trialText,
                        });
                        return;
                      }

                      downLoadFile();
                    }}
                    className="-mr-2 -ml-3 h-8 min-w-0 pr-2 pl-3 text-[11px]/[16px] font-medium"
                    intent="tertiary"
                    size="sm"
                  >
                    <div
                      className={twMerge(
                        "flex items-center gap-x-2",
                        (isDownloading || leadsTable.leads.length == 0) &&
                          "text-theme-on-background-variant",
                      )}
                    >
                      <span className="text-[11px]">Download</span>
                      {isDownloading ? (
                        <LoaderCircleIcon className="size-4 animate-spin" />
                      ) : (
                        <Download size={16} />
                      )}
                    </div>
                  </Button>
                </div>
              </div>

              <div className="border-separator mt-4 mb-3" />
            </div>

            <LeadsTableHeader
              id="scroll-headers"
              ref={headerScrollRef}
              className="overflow-x-scroll"
            />
          </StickyElement>

          <LeadsTableBody
            id="scroll-headers"
            ref={bodyScrollRef}
            className="min-w-0 overflow-x-scroll"
          />

          <div className="grid h-14 shrink-0 grow px-14 py-5 *:col-start-1 *:row-start-1">
            <AnimatePresence>
              {(() => {
                let key = "";
                let text = "";

                if (leadsQuery.isFetching) {
                  key = "loading";
                  text = "Henter leads...";
                } else if (!leadsTable.leads.length) {
                  if (leadsTable.errorText) {
                    key = "error";
                    text = leadsTable.errorText;
                  } else {
                    key = "no-results";
                    text = "Ingen leads fundet";
                  }
                }

                if (!key) return null;

                return (
                  <motion.div
                    key={key}
                    className="flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <span
                      className={twMerge(
                        "text-theme-on-background-variant text-center text-xs font-medium",
                        key === "loading" && "animate-pulse",
                      )}
                    >
                      {text}
                    </span>
                  </motion.div>
                );
              })()}
            </AnimatePresence>
          </div>

          {!leadsQuery.isFetching && leadsQuery.hasNextPage && (
            <div ref={leadsTable.bottomIntersectionObserver.ref} />
          )}
        </LeadsTable>
      </motion.div>
    </PageHeaderProvider>
  );
}

function useCampaignLeadsTable({
  campaign,
  usersQuery,
  sortDescriptor,
  setSortDescriptor,
  scrollEl,
  leads,
  isFetching,
  errorText,
  fetchNextPage,
  setTrialCTAModalProps,
}: {
  campaign: Campaign;
  usersQuery: UseSuspenseInfiniteQueryResult<
    InfiniteData<PagedResult<User>, unknown>,
    Error
  >;
  sortDescriptor: SortDescriptor;
  setSortDescriptor: (sortDescriptor: SortDescriptor) => void;
  scrollEl: HTMLElement | null;
  leads: (Lead | typeof ghostItem)[];
  isFetching?: boolean;
  errorText?: string;
  fetchNextPage?: () => void;
  setTrialCTAModalProps: (props: { title: string }) => void;
}) {
  const context = Route.useRouteContext();
  const search = Route.useSearch();

  const { data: appConfig } = useAppConfiguration();

  const omitColumns = useMemo(
    () => ({
      campaignName: true,
      responsible: true,
    }),
    [],
  );

  const campaigns = useMemo(() => (campaign ? [campaign] : []), [campaign]);

  const users = useMemo(
    () => usersQuery.data?.pages.flatMap((page) => page.results) ?? [],
    [usersQuery.data],
  );

  const { buildLeadLink, onClickLead } = useMemo(
    () =>
      context.trialPeriod?.isExpired
        ? {
            buildLeadLink: undefined,
            onClickLead: () =>
              setTrialCTAModalProps({
                title:
                  "Din prøveperiode er udløbet. Kontakt os for at få adgang til Lasso Sales uden begrænsninger.",
              }),
          }
        : {
            buildLeadLink: (lead: Lead): NavigateOptions => ({
              to: "/player/$id",
              params: { id: lead.id },
              search: {
                ...search,
                campaignId: [campaign.id],
                createdBefore: context.leadsCreatedBefore.toISOString(),
                returnTo: "campaign",
              },
            }),
            onClickLead: undefined,
          },
    [
      context.trialPeriod?.isExpired,
      context.leadsCreatedBefore,
      setTrialCTAModalProps,
      search,
      campaign.id,
    ],
  );

  const bottomIntersectionObserver = useIntersectionObserver({
    root: scrollEl,
    rootMargin: "24px",
  });

  useEffect(() => {
    if (!bottomIntersectionObserver.isIntersecting || isFetching) return;
    fetchNextPage?.();
  }, [bottomIntersectionObserver.isIntersecting, isFetching, fetchNextPage]);

  return {
    props: {
      omitColumns,
      leads,
      campaigns,
      appConfig,
      users,
      sortDescriptor,
      setSortDescriptor,
      buildLeadLink,
      onClickLead,
    } satisfies React.ComponentProps<typeof LeadsTable>,
    leads,
    bottomIntersectionObserver,
    errorText,
  };
}
